import { StandardEvents } from '@shopify/events';

export function confirmedCartCount(result) {
  const count = result?.cart?.totalQuantity;
  return Number.isSafeInteger(count) && count >= 0 ? count : null;
}

const pending = new WeakMap();
let cartRevision = 0;
let renderedRevision = 0;
let activeRequests = 0;
let noticeTimer;
const config = () => document.getElementById('do3d-cart-feedback-config');
const cartLinks = () => document.querySelectorAll('.d3-cart');

function renderCount(count) {
  if (!Number.isSafeInteger(count) || count < 0) return;
  for (const badge of document.querySelectorAll('[data-d3-cart-count]')) badge.textContent = String(count);
}

function renderBusy() {
  for (const link of cartLinks()) {
    if (activeRequests) link.setAttribute('aria-busy', 'true');
    else link.removeAttribute('aria-busy');
  }
}

function notice(message, error = false) {
  let element = document.getElementById('do3d-cart-feedback-notice');
  if (!element) {
    element = document.createElement('div');
    element.id = 'do3d-cart-feedback-notice';
    element.className = 'do3d-cart-feedback-notice';
    document.body.append(element);
  }
  element.textContent = message;
  element.hidden = false;
  element.toggleAttribute('data-error', error);
  if (error) {
    element.removeAttribute('aria-hidden');
    element.setAttribute('role', 'alert');
  } else {
    element.setAttribute('aria-hidden', 'true');
    element.removeAttribute('role');
  }
  clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => { element.hidden = true; }, error ? 10000 : 4000);
}

function beginFeedback(owner) {
  const state = { buttons: new Map(), observer: null };
  pending.set(owner, state);
  const updateButtons = () => {
    if (pending.get(owner) !== state) return;
    for (const button of owner.querySelectorAll('[name="add"]')) {
      if (!state.buttons.has(button)) {
        state.buttons.set(button, {
          label: button.getAttribute('aria-label'),
          disabled: button.getAttribute('aria-disabled'),
          color: getComputedStyle(button).color
        });
      }
      const original = state.buttons.get(button);
      button.style.setProperty('--do3d-feedback-color', original.color);
      button.dataset.do3dCartState = 'pending';
      button.dataset.do3dCartLabel = config().dataset.adding;
      button.setAttribute('aria-label', config().dataset.adding);
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.removeAttribute('data-added');
    }
  };
  updateButtons();
  state.observer = new MutationObserver(updateButtons);
  state.observer.observe(owner, { childList: true, subtree: true });
  return (success) => {
    state.observer.disconnect();
    pending.delete(owner);
    for (const [button, original] of state.buttons) {
      button.removeAttribute('aria-busy');
      if (button.getAttribute('aria-disabled') === 'true') {
        if (original.disabled === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', original.disabled);
      }
      if (button.getAttribute('aria-label') === config().dataset.adding) {
        if (original.label === null) button.removeAttribute('aria-label');
        else button.setAttribute('aria-label', original.label);
      }
      delete button.dataset.do3dCartState;
      delete button.dataset.do3dCartLabel;
      button.style.removeProperty('--do3d-feedback-color');
    }
    if (success) notice(config().dataset.added);
  };
}

function guardPending(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const owner = target.closest('product-form-component');
  if (!owner || !pending.has(owner)) return;
  if (event.type === 'click' && !target.closest('[name="add"], .d3-buy-now')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
}

document.addEventListener('click', guardPending, true);
document.addEventListener('submit', guardPending, true);
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (!event.promise) return;
  const revision = ++cartRevision;
  activeRequests++;
  renderBusy();
  const owner = event.target instanceof Element ? event.target.closest('product-form-component') : null;
  const finish = owner && !pending.has(owner) ? beginFeedback(owner) : null;
  event.promise.then((result) => {
    const count = confirmedCartCount(result);
    if (revision >= renderedRevision && count !== null) {
      renderedRevision = revision;
      renderCount(count);
    }
    const failed = result?.detail?.didError || result?.userErrors?.length || count === null;
    finish?.(!failed);
    if (finish && failed) notice(config().dataset.error, true);
  }).catch(() => {
    finish?.(false);
    if (finish) notice(config().dataset.error, true);
  }).finally(() => {
    activeRequests--;
    renderBusy();
  });
});

window.addEventListener('pageshow', async (event) => {
  if (!event.persisted) return;
  const revision = ++cartRevision;
  try {
    const response = await fetch(config().dataset.cartUrl, {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
    });
    if (!response.ok) return;
    const cart = await response.json();
    if (revision === cartRevision) renderCount(cart.item_count);
  } catch {}
});
